import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Button, Spinner, Form, Tabs, Tab, Badge } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import { useTranslation } from "react-i18next";
import { saveGeneralSettingData, getGeneralSettingList } from "../../redux/actions/GeneralSettingActions";
import FormInput from "../../Components/FormInput";
import { useNavigate, useParams } from "react-router-dom";
import { logout } from "../../redux/actions/AuthActions";
import { toast } from "react-toastify";
import Select from "react-dropdown-select";
import swal from 'sweetalert2';
import { getDateTimeMysqlGivenFormatYmd } from '../../util/generalUtills';
import Preloader from "../../Components/Preloader";

const Dashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth, loading, errors },
    generalSettingReducer: {
      generalSettingLists,
      loadingGeneralSetting,
      errorsGeneralSetting,
      changedGeneralSettingStatus,
    },
  } = useSelector((state) => state);

  useEffect(() => {
    dispatch(getGeneralSettingList(auth?.accessToken));
  }, [auth]);


  const options = [
    { value: true, label: 'Enable', isDisabled: false },
    { value: false, label: 'Disable', isDisabled: false },
  ];

  const options2 = [
    { value: 'SMS', label: 'SMS', isDisabled: false },
    { value: 'Whatsapp', label: 'Whatsapp', isDisabled: false },
  ];

  const options3 = [
    { value: 'None', label: 'None', isDisabled: false },
    { value: 'Image', label: 'Image', isDisabled: false },
    { value: 'Video', label: 'Video', isDisabled: false },
  ];

  const mapProviderOptions = [
    { value: 'Google', label: 'Google Map', isDisabled: false },
    { value: 'OpenStreetMap', label: 'Open Map', isDisabled: false },
    { value: 'Satellite', label: 'Satellite Map', isDisabled: false },
    { value: 'Dark', label: 'Dark Map', isDisabled: false },
  ];

  
  const journayTypeOptions = [
    { value: 'Returning', label: 'Returning', isDisabled: false },
    { value: 'OneWay', label: 'OneWay', isDisabled: false },
    { value: 'RoundTrip', label: 'RoundTrip', isDisabled: false },
  ];

  const anprProviderOptions = [
    { value: 'ANPR', label: 'ANPR', isDisabled: false },
    { value: 'PLATE_RECOGNIZER', label: 'Plate Recognizer', isDisabled: false },
  ];

  // Manually typed, badge-style tag input for allowedVehicleTypes (comma-separated on the backend).
  const [vehicleTypeInput, setVehicleTypeInput] = useState("");

  const allowedVehicleTypesList = useMemo(() => {
    return (formData.allowedVehicleTypes || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }, [formData.allowedVehicleTypes]);

  const addVehicleType = (rawValue) => {
    const value = rawValue.trim();
    if (!value) return;
    if (allowedVehicleTypesList.some((v) => v.toLowerCase() === value.toLowerCase())) return;
    setFormData((prev) => ({ ...prev, allowedVehicleTypes: [...allowedVehicleTypesList, value].join(',') }));
    if (fieldErrors?.allowedVehicleTypes) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.allowedVehicleTypes;
        return newErrors;
      });
    }
  };

  const removeVehicleType = (value) => {
    setFormData((prev) => ({
      ...prev,
      allowedVehicleTypes: allowedVehicleTypesList.filter((v) => v !== value).join(','),
    }));
  };

  const handleVehicleTypeKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addVehicleType(vehicleTypeInput.replace(/,$/, ''));
      setVehicleTypeInput('');
    } else if (e.key === 'Backspace' && !vehicleTypeInput && allowedVehicleTypesList.length > 0) {
      removeVehicleType(allowedVehicleTypesList[allowedVehicleTypesList.length - 1]);
    }
  };

  const handleVehicleTypeBlur = () => {
    if (vehicleTypeInput.trim()) {
      addVehicleType(vehicleTypeInput);
      setVehicleTypeInput('');
    }
  };

  const [validated, setValidated] = useState(false);

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
    const payload = {
      ...formData,
      locationInterval: Number(formData.locationInterval),
      videoRecordingTime: Number(formData.videoRecordingTime),
      cameraCaptureInterval: Number(formData.cameraCaptureInterval),
      maxDistanceHydrant: Number(formData.maxDistanceHydrant),
      maxDistanceDestination: Number(formData.maxDistanceDestination),
      iosVersion: Number(formData.iosVersion),
      androidVersion: Number(formData.androidVersion),
      defaultSms: Number(formData.defaultSms),
      nvrTimeoutSeconds: Number(formData.nvrTimeoutSeconds),
      anprMatchThreshold: Number(formData.anprMatchThreshold),
      mapDefaultLatitude: Number(formData.mapDefaultLatitude),
      mapDefaultLongitude: Number(formData.mapDefaultLongitude),
      mapDefaultZoom: Number(formData.mapDefaultZoom),
    };
    dispatch(saveGeneralSettingData(auth?.accessToken, payload));
  };

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      if (errors?.status === 401) {
        dispatch(logout());
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors, dispatch, navigate]);

  useEffect(() => {
    if (errorsGeneralSetting && Object.keys(errorsGeneralSetting).length > 0) {
      if (errorsGeneralSetting?.status === 409) {
        swal.fire({
          show: true,
          title: "Oops!",
          text: errorsGeneralSetting?.data?.message,
          icon: "error",
          showConfirmButton: false,
          timer: 1500
        });
        dispatch({ type: 'RESET_DELETE_GENERAL_SETTING_STATUS' });
      }
      else if (errorsGeneralSetting?.status === 400) {
        if (errorsGeneralSetting?.data?.errors && Array.isArray(errorsGeneralSetting.data.errors)) {
          const transformedErrors = {};
          errorsGeneralSetting.data.errors.forEach(error => {
            if (error.field && error.message) {
              transformedErrors[error.field] = error.message;
            }
          });
          // console.log(errorsGeneralSetting);
          setFieldErrors(transformedErrors);
        }
      }
      else {
        dispatch({ type: 'RESET_DELETE_GENERAL_SETTING_STATUS' });
      }
    }
  }, [errorsGeneralSetting, dispatch]);

  useEffect(() => {
    if (generalSettingLists && Object.keys(generalSettingLists).length > 0) {
      setFormData({
        companyName: generalSettingLists?.companyName,
        tittle: generalSettingLists?.tittle,
        url: generalSettingLists?.url,
        appUrl: generalSettingLists?.appUrl,
        homeUrl: generalSettingLists?.homeUrl,
        webBookingPrefix: generalSettingLists?.webBookingPrefix,
        appBookingPrefix: generalSettingLists?.appBookingPrefix,
        defaultSms: generalSettingLists?.defaultSms,
        locationInterval:generalSettingLists?.locationInterval,
        androidUpdateUrl: generalSettingLists?.androidUpdateUrl,
        iosUpdateUrl: generalSettingLists?.iosUpdateUrl,
        voiceNotification: generalSettingLists?.voiceNotification,
        videoRecordingTime: generalSettingLists?.videoRecordingTime,
        rejectBookingGpsOff: generalSettingLists?.rejectBookingGpsOff,
        rejectBookingInternetOff: generalSettingLists?.rejectBookingInternetOff,
        maxDistanceHydrant:generalSettingLists?.maxDistanceHydrant,
        maxDistanceDestination:generalSettingLists?.maxDistanceDestination,
        iosVersion:generalSettingLists?.iosVersion,
        androidVersion:generalSettingLists?.androidVersion,
        journayType:generalSettingLists?.journayType,
        hydrantNoOpenByApp:generalSettingLists?.hydrantNoOpenByApp,
        cameraCaptureInterval:generalSettingLists?.cameraCaptureInterval,
        requestForOpenValveImageRequired:generalSettingLists?.requestForOpenValveImageRequired,
        requestForSlipImageRequired:generalSettingLists?.requestForSlipImageRequired,
        waterSupplyEndType:generalSettingLists?.waterSupplyEndType,
        nvrTimeoutSeconds:generalSettingLists?.nvrTimeoutSeconds,
        anprEnabled:generalSettingLists?.anprEnabled,
        identifyNumberPlate:generalSettingLists?.identifyNumberPlate,
        validateNumberPlate:generalSettingLists?.validateNumberPlate,
        anprMatchThreshold:generalSettingLists?.anprMatchThreshold,
        anprProvider:generalSettingLists?.anprProvider,
        allowedVehicleTypes:generalSettingLists?.allowedVehicleTypes,
        mapProvider:generalSettingLists?.mapProvider,
        mapDefaultCity:generalSettingLists?.mapDefaultCity,
        mapDefaultLatitude:generalSettingLists?.mapDefaultLatitude,
        mapDefaultLongitude:generalSettingLists?.mapDefaultLongitude,
        mapDefaultZoom:generalSettingLists?.mapDefaultZoom,
      });
    }
  }, [generalSettingLists]);

  useEffect(() => {
    if (changedGeneralSettingStatus?.success) {
      swal.fire({
        show: true,
        title: "Well done!",
        text: changedGeneralSettingStatus?.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      dispatch({ type: 'RESET_DELETE_GENERAL_SETTING_STATUS' });
      dispatch(getGeneralSettingList(auth?.accessToken));
    }
  }, [changedGeneralSettingStatus, dispatch, navigate]);

  useEffect(() => {
    setFormData({});
    dispatch({ type: 'RESET_DELETE_GENERAL_SETTING_STATUS' });
  }, [dispatch]);

  const inputHandler = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const inputHandlerCaps = (e) => {
    const { value, name } = e.target;
    const processedValue = value.replace(/\s+/g, '').toUpperCase(); // Remove spaces and convert to uppercase
    setFormData({ ...formData, [name]: processedValue });
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const selectHandler = (selectedData, name) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: selectedData[0].value,
    }));
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      '&:hover': {
        borderColor: errors?.data?.state_code ? 'red' : base.borderColor,
      }
    }),
  };

  const onDateChange = (name, value) => {
    if (value) {
      setFormData({ ...formData, [name]: getDateTimeMysqlGivenFormatYmd(value) });
    }
  };
  useEffect(() => { 
     console.log('fieldErrors', fieldErrors);
   },[fieldErrors]);

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: t("General Setting"), path: "General Setting" },
        ]}
        title={t("General Setting")}
      />
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Body>
              {errorsGeneralSetting?.data?.message && (
                <div className="alert alert-danger">
                  {errorsGeneralSetting?.data?.message}
                </div>
              )}

              <Tabs
                defaultActiveKey="general"
                id="company-settings-tabs"
                className="mb-3"
                transition={false}
                unmountOnExit
              >
                <Tab eventKey="general" title={t("General Information")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <FormInput
                    label={t("Company Name")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.companyName}
                    name="companyName"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Title")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.tittle}
                    name="tittle"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Web Url")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.url}
                    name="url"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("App Url")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.appUrl}
                    name="appUrl"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Home Url")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.homeUrl}
                    name="homeUrl"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

           

                

                </Row>
                </Tab>
                <Tab eventKey="booking" title={t("Booking Prefix & Limits")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <FormInput
                    label={t("Web Booking Prefix")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.webBookingPrefix}
                    name="webBookingPrefix"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("App Booking Prefix")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.appBookingPrefix}
                    name="appBookingPrefix"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                

                

                    

                <Col lg={3}>
                  <FormInput
                    label={t("Default SMS")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.defaultSms}
                    name="defaultSms"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
{/* 
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Sent Complete SMS")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.sentCompleteSms)}
                      name="sentCompleteSms"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "sentCompleteSms")}
                      styles={customSelectStyles}
                    />

                    {fieldErrors?.sent_complete_sms && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.sent_complete_sms}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Change Booking Date")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.changeBookingDate)}
                      name="changeBookingDate"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "changeBookingDate")}
                      styles={customSelectStyles}
                    />

                    {fieldErrors?.changeBookingDate && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.changeBookingDate}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("Normal Customer")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.normal_customer}
                    name="normal_customer"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("Special Customer")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.special_customer}
                    name="special_customer"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("VIP Customer")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.vip_customer}
                    name="vip_customer"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("VVIP Customer")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.vvip_customer}
                    name="vvip_customer"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Customer Complete")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.customer_complete)}
                      name="customer_complete"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "customer_complete")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.customer_complete && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.customer_complete}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("Missed Call Number")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.missed_call_number}
                    name="missed_call_number"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

              

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Block Duplicate Booking")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.block_duplicate_booking)}
                      name="block_duplicate_booking"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "block_duplicate_booking")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.block_duplicate_booking && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.block_duplicate_booking}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Blacklist Status")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.blacklist_status)}
                      name="blacklist_status"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "blacklist_status")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.blacklist_status && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.blacklist_status}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                </Row>
                </Tab>
                <Tab eventKey="location" title={t("Location & Distance Settings")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <FormInput
                    label={t("Location Interval")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.locationInterval}
                    name="locationInterval"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
                 <Col lg={3}>
                  <FormInput
                    label={t("Max Distance Hydrant")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.maxDistanceHydrant}
                    name="maxDistanceHydrant"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Max Distance Destination")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.maxDistanceDestination}
                    name="maxDistanceDestination"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
                </Row>
                </Tab>
                <Tab eventKey="mobile" title={t("Mobile App Settings")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <FormInput
                    label={t("iOS Version")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.iosVersion}
                    name="iosVersion"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
                <Col lg={3}>
                  <FormInput
                    label={t("Android Version")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.androidVersion}
                    name="androidVersion"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>


          

                <Col lg={3}>
                  <FormInput
                    label={t("Android Update Url")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.androidUpdateUrl}
                    name="androidUpdateUrl"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
                <Col lg={3}>
                  <FormInput
                    label={t("iOS Update Url")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.iosUpdateUrl}
                    name="iosUpdateUrl"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Voice Notification")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.voiceNotification)}
                      name="voiceNotification"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "voiceNotification")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.voice_notification && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.voice_notification}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("NFC Reader Hydrant")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.nfc_reader_hydrant)}
                      name="nfc_reader_hydrant"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "nfc_reader_hydrant")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.nfc_reader_hydrant && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.nfc_reader_hydrant}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("NFC Reader Destination")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.nfc_reader_destination)}
                      name="nfc_reader_destination"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "nfc_reader_destination")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.nfc_reader_destination && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.nfc_reader_destination}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                </Row>
                </Tab>
                <Tab eventKey="camera" title={t("Camera & ANPR Settings")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <FormInput
                    label={t("Video Recording Time")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.videoRecordingTime}
                    name="videoRecordingTime"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Camera Capture Interval")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.cameraCaptureInterval}
                    name="cameraCaptureInterval"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("NVR Timeout (seconds)")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.nvrTimeoutSeconds}
                    name="nvrTimeoutSeconds"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Water Supply End Type")}</Form.Label>
                    <Select
                      options={options3}
                      values={options3.filter((opt) => opt.value === formData.waterSupplyEndType)}
                      name="waterSupplyEndType"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "waterSupplyEndType")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.waterSupplyEndType && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.waterSupplyEndType}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("ANPR Enabled")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.anprEnabled)}
                      name="anprEnabled"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "anprEnabled")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.anprEnabled && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.anprEnabled}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("ANPR Provider")}</Form.Label>
                    <Select
                      options={anprProviderOptions}
                      values={anprProviderOptions.filter((opt) => opt.value === formData?.anprProvider)}
                      name="anprProvider"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "anprProvider")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.anprProvider && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.anprProvider}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("ANPR Match Threshold (%)")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.anprMatchThreshold}
                    name="anprMatchThreshold"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Identify Number Plate")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.identifyNumberPlate)}
                      name="identifyNumberPlate"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "identifyNumberPlate")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.identifyNumberPlate && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.identifyNumberPlate}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Validate Number Plate")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.validateNumberPlate)}
                      name="validateNumberPlate"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "validateNumberPlate")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.validateNumberPlate && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.validateNumberPlate}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Allowed Vehicle Types (Plate Recognizer)")}</Form.Label>
                    {allowedVehicleTypesList.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {allowedVehicleTypesList.map((vt) => (
                          <Badge key={vt} bg="secondary" className="d-flex align-items-center py-2 px-2">
                            {vt}
                            <span
                              role="button"
                              aria-label={t("Remove")}
                              onClick={() => removeVehicleType(vt)}
                              style={{ cursor: "pointer", marginLeft: 8 }}
                            >
                              &times;
                            </span>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <Form.Control
                      type="text"
                      placeholder={t("Type a vehicle type and press Enter or comma (e.g. Truck, Tractor, Big Truck)")}
                      value={vehicleTypeInput}
                      onChange={(e) => setVehicleTypeInput(e.target.value)}
                      onKeyDown={handleVehicleTypeKeyDown}
                      onBlur={handleVehicleTypeBlur}
                    />
                    <Form.Text muted>
                      {t("Leave empty to allow any vehicle type. Otherwise, only tankers detected as one of these types (case-insensitive) can open the valve.")}
                    </Form.Text>
                    {fieldErrors?.allowedVehicleTypes && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.allowedVehicleTypes}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                </Row>
                </Tab>
                <Tab eventKey="images" title={t("Image & Slip Requirements")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Request For Open Valve Image Required")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.requestForOpenValveImageRequired)}
                      name="requestForOpenValveImageRequired"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "requestForOpenValveImageRequired")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.requestForOpenValveImageRequired && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.requestForOpenValveImageRequired}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Request For Slip Image Required")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData?.requestForSlipImageRequired)}
                      name="requestForSlipImageRequired"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "requestForSlipImageRequired")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.requestForSlipImageRequired && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.requestForSlipImageRequired}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

          

                </Row>
                </Tab>
                <Tab eventKey="restrictions" title={t("Booking Restrictions")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Reject Booking GPS Off")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.rejectBookingGpsOff)}
                      name="rejectBookingGpsOff"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "rejectBookingGpsOff")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.rejectBookingGpsOff && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.rejectBookingGpsOff}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Reject Booking Internet Off")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.rejectBookingInternetOff)}
                      name="rejectBookingInternetOff"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "rejectBookingInternetOff")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.rejectBookingInternetOff && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.rejectBookingInternetOff}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Booking Approval")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.booking_approval)}
                      name="booking_approval"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "booking_approval")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.booking_approval && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.booking_approval}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("A.E.N / J.E.N Approval")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.aen_jen_approval)}
                      name="aen_jen_approval"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "aen_jen_approval")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.aen_jen_approval && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.aen_jen_approval}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Send Remark Mail")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.send_remark_mail)}
                      name="send_remark_mail"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "send_remark_mail")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.send_remark_mail && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.send_remark_mail}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

                {/* <Col lg={3}>
                  <FormInput
                    label={t("Get Otp via Misscall")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.get_otp_via_misscall}
                    name="get_otp_via_misscall"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}

                {/* <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Aen/Jen Booking Approval By")}</Form.Label>
                    <Select
                      options={options2}
                      values={options2.filter((opt) => opt.value === formData.aen_jen_otp_via)}
                      name="aen_jen_otp_via"
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "aen_jen_otp_via")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.aen_jen_otp_via && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.aen_jen_otp_via}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col> */}

          

                 <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Journey Type")}</Form.Label>
                    <Select
                      options={journayTypeOptions}
                      values={journayTypeOptions.filter((opt) => opt.value === formData.journayType)}
                      name="journayType"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "journayType")}
                      styles={customSelectStyles}
                    />
                  </Form.Group>
                  {fieldErrors?.journayType && (
                    <div className="invalid-feedback d-block">
                      <p className="text-danger">{fieldErrors?.journayType}</p>
                    </div>
                  )}
                </Col>

                {/* <Col lg={3}>
                  <FormInput
                    label={t("Journey Distance Calculation")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.journay_distance_calculation}
                    name="journay_distance_calculation"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col> */}
                
                    <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Hydrant Nob Open By App")}</Form.Label>
                    <Select
                      options={options}
                      values={options.filter((opt) => opt.value === formData.hydrantNoOpenByApp)}
                      name="hydrantNoOpenByApp"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "hydrantNoOpenByApp")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.hydrantNoOpenByApp && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.hydrantNoOpenByApp}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                </Row>
                </Tab>

                <Tab eventKey="map" title={t("Map Settings")}>
                <Row className="mt-3">
                <Col lg={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("Default Map")}</Form.Label>
                    <Select
                      options={mapProviderOptions}
                      values={mapProviderOptions.filter((opt) => opt.value === formData?.mapProvider)}
                      name="mapProvider"
                      portal={typeof document !== "undefined" ? document.body : null}
                      searchable
                      addPlaceholder={t("Search...")}
                      labelField={"label"}
                      valueField={"value"}
                      onChange={(selectedData) => selectHandler(selectedData, "mapProvider")}
                      styles={customSelectStyles}
                    />
                    {fieldErrors?.mapProvider && (
                      <div className="invalid-feedback d-block">
                        <p className="text-danger">{fieldErrors?.mapProvider}</p>
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Default City")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.mapDefaultCity}
                    name="mapDefaultCity"
                    onChange={inputHandler}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Default Latitude")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.mapDefaultLatitude}
                    name="mapDefaultLatitude"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Default Longitude")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.mapDefaultLongitude}
                    name="mapDefaultLongitude"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>

                <Col lg={3}>
                  <FormInput
                    label={t("Default Zoom (1-20)")}
                    type="text"
                    errors={fieldErrors}
                    value={formData.mapDefaultZoom}
                    name="mapDefaultZoom"
                    onChange={inputHandlerCaps}
                    containerClass={"mb-3"}
                  />
                </Col>
                </Row>
                </Tab>
              </Tabs>

              <div className="d-flex justify-content-center">
                {loading ?
                  <Button color="primary" disabled>
                    <Spinner
                      className="spinner-border-sm me-1"
                      tag="span"
                      color="white"
                    />
                    Please wait ...
                  </Button>
                  :
                  <Button type="button" onClick={handleSubmit} disabled={loading}>
                    Submit
                  </Button>
                }
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {loadingGeneralSetting && (<Preloader />)}
    </>
  );
};

export default Dashboard;
